import { IconAlertTriangle, IconInfoCircle } from "@tabler/icons-react";
import { useLocale, useTranslations } from "next-intl";
import { safeLanguageName } from "@/lib/utils/languages";
import type { RecipientLanguage } from "../utils/template-utils";

/**
 * The language ONE recipient will actually receive, named out loud.
 *
 * The send dialogs offer no per-send language: the worker renders every
 * recipient in their own `guest.language` and falls back to the template's
 * stored primary. Which of the two happened was invisible here, so a template
 * switch or a guest with an empty language field looked like the dialog
 * ignoring the admin. This states it per row.
 *
 * Three things are worth spelling out, and each points at a DIFFERENT repair:
 *
 *   - no language on the guest        → fix the guest record
 *   - no such variant in the template → add the variant
 *   - the variant exists but says the same thing as the primary → translate it
 *
 * The third is the one this component was rebuilt for. A declared-but-never-
 * written language is baked with the primary language's prose under its own key
 * (`AppBarEmailBuilder` → `parse-email.ts`), so it used to render here as a
 * clean "English" with no warning at all while the recipient received German.
 *
 * The sentence is deliberately about observable content ("identical to German"),
 * not about intent ("untranslated"): an image-only mail is legitimately
 * identical in every language, and the sentence stays true there.
 */
export const RecipientLanguageChip: React.FC<{
  language: RecipientLanguage;
  /** The guest's own language tag — named in the "no such variant" case so the
   *  admin knows which variant is missing. */
  guestLanguage?: string | null;
  className?: string;
}> = ({ language, guestLanguage, className }) => {
  const t = useTranslations("pSendEmail");
  const locale = useLocale();
  if (!language.code) return null;

  const name = safeLanguageName(language.code, locale);
  // Everything the sentence names comes from the RESOLUTION, never from the
  // template's declared primary. A template can hold no content for its own
  // primary language, and a sentence naming a language the resolution never
  // touched was the second falsehood this component shipped.
  const receives = name;
  const comparedName = safeLanguageName(language.comparedTo ?? "", locale);

  const explanation =
    language.state === "fallback-no-language"
      ? t("recipientLanguageFallback", { fallback: receives })
      : language.state === "fallback-no-variant"
        ? t("recipientLanguageNoVariant", {
            language: safeLanguageName(guestLanguage ?? "", locale),
            fallback: receives,
          })
        : language.state === "identical"
          ? t("recipientLanguageIdentical", { fallback: comparedName })
          : null;

  const Icon =
    language.state === "identical"
      ? IconAlertTriangle
      : language.state === "own"
        ? null
        : IconInfoCircle;

  return (
    <span
      data-testid="recipient-language"
      data-state={language.state}
      title={explanation ?? undefined}
      className={`shrink-0 flex items-center gap-1 body-3-regular ${
        language.state === "identical" ? "font-error" : "font-tertiary"
      } ${language.state === "own" ? "" : "italic"} ${className ?? ""}`}
    >
      {Icon && <Icon size={12} className="shrink-0" aria-hidden="true" />}
      {name}
      {/* The explanation lived only in `title` on a non-focusable span, with the
          icon aria-hidden — leaving italics as the sole carrier of the one thing
          this component exists to say. Assistive tech gets the sentence. */}
      {explanation && <span className="sr-only"> — {explanation}</span>}
    </span>
  );
};
